// Loads the names (and, for trusts, the school lists) of the organisations a user's access reaches.
// Identity scopes carry ids only; the Admin-Api holds the names. Each lookup is independent and
// failure-tolerant: a trust that can't be loaded is simply absent from `groupSchoolIdsById`, which
// the edit rules treat as an incomplete picture.

import matGroupService from '../../services/api/matGroupService';
import schoolService from '../../services/api/schoolService';

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// The same school list the trust page reads, with the flatter shapes tolerated too.
const readGroupSchools = (data) =>
  data?.Schools || data?.schools || data?.MatGroup?.Schools || data?.GroupDirectors?.[0]?.MatGroup?.Schools || [];

export async function loadGroup(groupId) {
  const response = await matGroupService.getMatById(groupId);
  const data = response?.success === false ? null : response?.data ?? null;
  if (!data) return null;
  const schools = readGroupSchools(data)
    .map((school) => ({ id: toNumber(school?.Id ?? school?.id), name: school?.Name ?? school?.name ?? '' }))
    .filter((school) => school.id !== null);
  return { name: data.Name ?? data.name ?? '', schools };
}

export async function loadSchoolName(schoolId) {
  const response = await schoolService.getInfo(schoolId);
  const data = response?.success === false ? null : response?.data ?? null;
  return data ? data.Name ?? data.name ?? '' : '';
}

// Resolves every trust the user is granted on, then names the schools that sit outside those
// trusts. `known` supplies what the page already holds ({ groups: {id: {name, schools}},
// schoolNamesById }) so the page's own organisation isn't fetched again.
export async function loadOrganisationDetails({ groupIds = [], schoolIds = [], known = {} }) {
  const groupsById = { ...(known.groups || {}) };
  const schoolNamesById = { ...(known.schoolNamesById || {}) };

  await Promise.all(
    groupIds
      .filter((id) => !groupsById[id])
      .map((id) =>
        loadGroup(id)
          .then((group) => {
            if (group) groupsById[id] = group;
          })
          .catch(() => {})
      )
  );

  Object.values(groupsById).forEach((group) =>
    group.schools.forEach((school) => {
      if (school.name && !schoolNamesById[school.id]) schoolNamesById[school.id] = school.name;
    })
  );

  await Promise.all(
    schoolIds
      .filter((id) => !schoolNamesById[id])
      .map((id) =>
        loadSchoolName(id)
          .then((name) => {
            if (name) schoolNamesById[id] = name;
          })
          .catch(() => {})
      )
  );

  const groupSchoolIdsById = Object.fromEntries(
    Object.entries(groupsById).map(([id, group]) => [id, group.schools.map((school) => school.id)])
  );
  const groupNamesById = Object.fromEntries(Object.entries(groupsById).map(([id, group]) => [id, group.name]));

  return { groupSchoolIdsById, groupNamesById, schoolNamesById };
}
